import serialize from 'canonicalize'
import { CryptoError } from './errors.js'

/**
 * RFC 8785 JSON Canonicalization Scheme — PRD §7.4.
 * Serialization delegates to the vetted `canonicalize` implementation; this
 * module owns the strict validation the PRD requires: duplicate keys, invalid
 * Unicode and unsupported numbers are rejected, never normalized or silently
 * dropped.
 */

export type JcsValue = null | boolean | number | string | readonly JcsValue[] | JcsObject
export interface JcsObject {
  readonly [key: string]: JcsValue
}

function loneSurrogateAt(value: string): number {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1)
      if (!(next >= 0xdc00 && next <= 0xdfff)) return index
      index += 1
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      return index
    }
  }
  return -1
}

function unsupported(message: string): CryptoError {
  return new CryptoError('CRYPTO_JCS_UNSUPPORTED_VALUE', message)
}

function parseError(message: string): CryptoError {
  return new CryptoError('CRYPTO_JSON_PARSE_ERROR', message)
}

/**
 * Rejects any value outside the I-JSON data model JCS can serialize:
 * non-finite numbers, undefined/function/symbol/bigint, sparse arrays,
 * non-plain objects, `toJSON` hooks and lone surrogates.
 */
export function assertJcsValue(value: unknown, path = '$'): asserts value is JcsValue {
  if (value === null) return
  switch (typeof value) {
    case 'boolean':
      return
    case 'number':
      if (!Number.isFinite(value)) throw unsupported(`${path}: non-finite numbers are not allowed`)
      return
    case 'string':
      if (loneSurrogateAt(value) >= 0)
        throw unsupported(`${path}: lone surrogate is not valid Unicode`)
      return
    case 'object': {
      if (Array.isArray(value)) {
        for (let index = 0; index < value.length; index += 1) {
          if (!(index in value))
            throw unsupported(`${path}[${index}]: sparse arrays are not allowed`)
          assertJcsValue(value[index], `${path}[${index}]`)
        }
        return
      }
      const prototype = Object.getPrototypeOf(value)
      if (prototype !== Object.prototype && prototype !== null) {
        throw unsupported(`${path}: only plain JSON objects are allowed`)
      }
      if (typeof (value as { toJSON?: unknown }).toJSON === 'function') {
        throw unsupported(`${path}: toJSON is not part of the JSON data model`)
      }
      if (Object.getOwnPropertySymbols(value).length > 0) {
        throw unsupported(`${path}: symbol keys are not allowed`)
      }
      for (const key of Object.keys(value)) {
        if (loneSurrogateAt(key) >= 0) throw unsupported(`${path}: lone surrogate in key`)
        assertJcsValue((value as Record<string, unknown>)[key], `${path}.${key}`)
      }
      return
    }
    default:
      throw unsupported(`${path}: unsupported value type ${typeof value}`)
  }
}

export function canonicalizeJcs(value: unknown): string {
  assertJcsValue(value)
  const canonical = serialize(value)
  if (typeof canonical !== 'string') {
    throw unsupported('value has no JSON canonical form')
  }
  return canonical
}

class StrictJsonParser {
  private index = 0

  constructor(private readonly text: string) {}

  parse(): JcsValue {
    this.skipWhitespace()
    const value = this.parseValue()
    this.skipWhitespace()
    if (this.index !== this.text.length) {
      throw parseError(`unexpected trailing characters at index ${this.index}`)
    }
    return value
  }

  private parseValue(): JcsValue {
    this.skipWhitespace()
    const char = this.text[this.index]
    if (char === undefined) throw parseError('unexpected end of input')
    if (char === '{') return this.parseObject()
    if (char === '[') return this.parseArray()
    if (char === '"') return this.parseString()
    if (char === 't') {
      this.expectLiteral('true')
      return true
    }
    if (char === 'f') {
      this.expectLiteral('false')
      return false
    }
    if (char === 'n') {
      this.expectLiteral('null')
      return null
    }
    if (char === '-' || (char >= '0' && char <= '9')) return this.parseNumber()
    throw parseError(`unexpected character ${JSON.stringify(char)} at index ${this.index}`)
  }

  private parseObject(): JcsObject {
    this.index += 1
    const result: Record<string, JcsValue> = {}
    const keys = new Set<string>()
    this.skipWhitespace()
    if (this.text[this.index] === '}') {
      this.index += 1
      return result
    }
    for (;;) {
      this.skipWhitespace()
      if (this.text[this.index] !== '"') throw parseError('object keys must be strings')
      const key = this.parseString()
      if (keys.has(key)) {
        throw new CryptoError(
          'CRYPTO_JSON_DUPLICATE_KEY',
          `duplicate object key ${JSON.stringify(key)}`,
        )
      }
      keys.add(key)
      this.skipWhitespace()
      if (this.text[this.index] !== ':') throw parseError('expected ":" after object key')
      this.index += 1
      const value = this.parseValue()
      // defineProperty keeps `__proto__` and `constructor` as ordinary data keys.
      Object.defineProperty(result, key, {
        value,
        enumerable: true,
        writable: true,
        configurable: true,
      })
      this.skipWhitespace()
      const next = this.text[this.index]
      if (next === ',') {
        this.index += 1
        continue
      }
      if (next === '}') {
        this.index += 1
        return result
      }
      throw parseError(`expected "," or "}" at index ${this.index}`)
    }
  }

  private parseArray(): JcsValue[] {
    this.index += 1
    const result: JcsValue[] = []
    this.skipWhitespace()
    if (this.text[this.index] === ']') {
      this.index += 1
      return result
    }
    for (;;) {
      result.push(this.parseValue())
      this.skipWhitespace()
      const next = this.text[this.index]
      if (next === ',') {
        this.index += 1
        this.skipWhitespace()
        continue
      }
      if (next === ']') {
        this.index += 1
        return result
      }
      throw parseError(`expected "," or "]" at index ${this.index}`)
    }
  }

  private parseString(): string {
    if (this.text[this.index] !== '"') throw parseError('expected string')
    this.index += 1
    let result = ''
    for (;;) {
      const char = this.text[this.index]
      if (char === undefined) throw parseError('unterminated string')
      if (char === '"') {
        this.index += 1
        break
      }
      if (char === '\\') {
        this.index += 1
        const escape = this.text[this.index]
        switch (escape) {
          case '"':
            result += '"'
            this.index += 1
            break
          case '\\':
            result += '\\'
            this.index += 1
            break
          case '/':
            result += '/'
            this.index += 1
            break
          case 'b':
            result += '\b'
            this.index += 1
            break
          case 'f':
            result += '\f'
            this.index += 1
            break
          case 'n':
            result += '\n'
            this.index += 1
            break
          case 'r':
            result += '\r'
            this.index += 1
            break
          case 't':
            result += '\t'
            this.index += 1
            break
          case 'u': {
            const hex = this.text.slice(this.index + 1, this.index + 5)
            if (!/^[0-9a-fA-F]{4}$/.test(hex)) throw parseError('invalid \\u escape')
            result += String.fromCharCode(Number.parseInt(hex, 16))
            this.index += 5
            break
          }
          default:
            throw parseError(`invalid escape sequence at index ${this.index}`)
        }
        continue
      }
      if (char.charCodeAt(0) < 0x20) throw parseError('unescaped control character in string')
      result += char
      this.index += 1
    }
    if (loneSurrogateAt(result) >= 0) {
      throw unsupported('string contains a lone surrogate')
    }
    return result
  }

  private parseNumber(): number {
    const match = /^-?(0|[1-9]\d*)(\.\d+)?([eE][+-]?\d+)?/.exec(this.text.slice(this.index))
    const literal = match?.[0]
    if (literal === undefined || literal.length === 0) throw parseError('invalid number')
    const value = Number(literal)
    if (!Number.isFinite(value)) throw parseError(`number ${literal} is not finite`)
    if (!literal.includes('.') && !/[eE]/.test(literal) && !Number.isSafeInteger(value)) {
      throw unsupported(`integer literal ${literal} exceeds the IEEE-754 exact range`)
    }
    this.index += literal.length
    return value
  }

  private expectLiteral(literal: string): void {
    if (!this.text.startsWith(literal, this.index)) {
      throw parseError(`expected ${literal} at index ${this.index}`)
    }
    this.index += literal.length
  }

  private skipWhitespace(): void {
    while (true) {
      const char = this.text[this.index]
      if (char === ' ' || char === '\t' || char === '\n' || char === '\r') {
        this.index += 1
        continue
      }
      return
    }
  }
}

/**
 * Strict JSON parse for canonicalization input. Unlike `JSON.parse`, duplicate
 * keys, lone surrogates and integer literals outside the exact IEEE-754 range
 * are rejected (last-wins/rounding would silently change signed content).
 */
export function parseJsonStrict(text: string): JcsValue {
  return new StrictJsonParser(text).parse()
}

/** Parse then canonicalize — the intended ingestion path for stored JSON text. */
export function canonicalizeJsonTextStrict(text: string): string {
  return canonicalizeJcs(parseJsonStrict(text))
}
